import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUiStrings } from '@/hooks/useUiStrings'
import { DEFAULT_SESSION_USER } from '@/constants/auth'
import { useStore } from '@/store/useStore'
import { resetBacklogUserId } from '@/services/backlogAccount'
import { LogoutIcon, SettingsIcon } from './Icons'

interface UserMenuProps {
  onNavigate?: () => void
}

export default function UserMenu({ onNavigate }: UserMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const user = useStore((s) => s.user)
  const logout = useStore((s) => s.logout)
  const profile = user ?? DEFAULT_SESSION_USER
  const initial = profile.displayName?.trim()?.[0]?.toUpperCase() ?? 'U'
  const { t } = useUiStrings()
  const isAdmin = user?.role === 'admin'

  useEffect(() => {
    if (!open) return
    const onDoc = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const handleLogout = () => {
    setOpen(false)
    logout()
    // This sign-out never reloads the document — it routes — so module state
    // survives it. The next person to sign in on this tab would otherwise
    // record a meeting stamped with the previous one's Backlog account.
    resetBacklogUserId()
    onNavigate?.()
    navigate('/login', { replace: true })
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex w-full touch-manipulation items-center gap-3 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-[var(--bg-tertiary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--border-color)] text-sm font-bold text-white shadow-sm"
          style={{ background: 'var(--msg-bg)' }}
        >
          {initial}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-[var(--text-primary)]">
            {profile.displayName}
          </span>
          <span className="block truncate text-[10px] font-medium uppercase tracking-[0.12em] text-[var(--text-tertiary)]">
            {profile.plan}
          </span>
        </span>
        <ChevronIcon
          className={[
            'h-4 w-4 shrink-0 text-[var(--text-tertiary)] transition-transform',
            open ? 'rotate-180' : '',
          ].join(' ')}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-[100] mb-2 w-full animate-fade-in rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2 shadow-[var(--shadow-lg)]"
        >
          {/* Language and theme used to sit here. They are per-browser
              preferences, not account actions, and they now live on the
              settings screen beside each other — this menu is the account:
              who you are, what an admin may run, and the way out. */}
          {/* Admin management only. The Excel export used to sit beside it and
              ran the same report the admin screen already offers, from a menu
              that had no say over which rows it covered — the admin screen's
              does. Two doors onto one report meant two places to keep right. */}
          {isAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                onNavigate?.()
                navigate('/admin')
              }}
              className="mt-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
            >
              <SettingsIcon className="h-4 w-4 shrink-0" />
              {t.adminManagement}
            </button>
          )}

          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="mt-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-sm text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)]"
          >
            <LogoutIcon className="h-4 w-4 shrink-0" />
            {t.logout}
          </button>
        </div>
      )}
    </div>
  )
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
    </svg>
  )
}
